// =============================================================================
// VandieZ agent-web -- My Owners page (/)
// =============================================================================
// Lists all active owner grants for the authenticated agent. Each card links
// to /vehicles?as=<ownerId> so the agent jumps straight into managing that
// owner's fleet. Shows a permissions summary line per card.
//
// Uses AppHeader with showOwnerSwitcher={false} since this page IS the owner
// picker — no need to duplicate the dropdown here.
// =============================================================================

import { Building2, ChevronRight, Loader2 } from "lucide-react";
import type React from "react";
import { Link } from "react-router-dom";

import { AppHeader } from "@/components/AppHeader";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useOwnerContext } from "@/lib/owner-context";

export function MyOwnersPage(): React.JSX.Element {
  const { owners, isLoading, error } = useOwnerContext();
  const active = owners.filter((o) => o.status === "active");

  return (
    <div className="min-h-screen bg-muted/30">
      <AppHeader title="My owners" showOwnerSwitcher={false} />
      <main className="mx-auto max-w-5xl space-y-6 px-6 py-10">
        {isLoading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : error !== null ? (
          <Card>
            <CardHeader>
              <CardTitle>{error}</CardTitle>
            </CardHeader>
          </Card>
        ) : active.length === 0 ? (
          <Card>
            <CardHeader className="items-center text-center">
              <Building2 className="h-10 w-10 text-muted-foreground" />
              <CardTitle>No owners assigned yet</CardTitle>
              <CardDescription>
                Once an owner invites you and you accept the link, they'll
                appear here. The owner sends the link via WhatsApp or SMS.
              </CardDescription>
            </CardHeader>
          </Card>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            {active.map((o) => (
              <Link key={o.owner_id} to={`/vehicles?as=${o.owner_id}`}>
                <Card className="group cursor-pointer transition-colors hover:border-primary/50">
                  <CardHeader className="flex flex-row items-center justify-between space-y-0">
                    <div>
                      <CardTitle className="text-base">{o.owner_full_name}</CardTitle>
                      <CardDescription>{o.owner_phone_masked}</CardDescription>
                    </div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
                  </CardHeader>
                  <CardContent>
                    <p className="text-xs text-muted-foreground">
                      Permissions:{" "}
                      {Object.entries(o.permissions)
                        .filter(([, v]) => Array.isArray(v) && (v as string[]).length > 0)
                        .map(([k, v]) => `${k}: ${(v as string[]).join("/")}`)
                        .join(" · ")}
                    </p>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
